import { useEffect } from 'react';
import { CreateVehicleSmartInsightResponse } from '@/api/requests';
import { useAppConfigContext } from '@/context/AppConfig';
import { useLLM } from '@/hooks/useLLM';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import { serializedTypes } from '@/reports/types';
import {
  AutomobileTitleRegistration,
  AutomobileTitleRegistrationCore,
  AutomobileTitleRegistrations,
} from '@/reports/types/vehicles';
import { HistoryBasedValueTooltip } from '@/reports/vehicle/HistoryBasedValueTooltip/HistoryBasedValueTooltip';
import { VSIHistoryBasedText } from '@/reports/vehicle/SmartInsights/VehicleSmartInsight/VSIHistoryBasedText/VSIHistoryBasedText';
import { ConditionalBadge } from '@/reports/vehicle/VehicleOverview/VehicleReportInfoCarousel/ConditionalBadge/ConditionalBadge';
import { Box, Icons, Stack, Text } from '@/theme/index';
import {
  commaFormat,
  commaFormatCurrency,
  pluralizeString,
  useComponentInView,
  useScreenSize,
} from '@/utils/index';
import { Polygon } from './Polygon';

interface VehicleValueProps {
  /**
   * The vehicle report containing data about the vehicle.
   */
  vehicleReport: serializedTypes.SerializedVehicleReport;
}

export function VehicleValue({ vehicleReport }: VehicleValueProps) {
  const { trackEventGA4 } = useAppConfigContext();
  const { isInView, hasBeenViewed, elementRef } = useComponentInView();
  const { isMobile } = useScreenSize();
  const currentVehicle = vehicleReport?.automobiles[0];
  const { data: llmResponse, isLoading } = useLLM(
    'vehicle',
    currentVehicle
  ) as unknown as {
    data: CreateVehicleSmartInsightResponse;
    isLoading: boolean;
    hasSmartInsights: boolean;
    fetchSmartInsights: () => void;
  };

  const typedLLMResponse = llmResponse as CreateVehicleSmartInsightResponse;

  useEffect(() => {
    if (isInView && !hasBeenViewed) {
      trackEventGA4({
        event_name: 'vehicle_value',
        type: 'vehicle_value_init',
      });
    }
  }, [isInView, hasBeenViewed]);

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  const isYMMSearch = vehicleReport.meta?.search_by === 'ymm';
  const averageMarketValue =
    currentVehicle?.valuation?.clean_retail?.adjusted ||
    currentVehicle?.prices.average ||
    0;

  const historyBasedValue = typedLLMResponse?.data?.historyBasedValue || 0;

  const accidentsCount =
    (currentVehicle?.accidentsCount || 0) +
    (currentVehicle?.salvagesCount || 0);
  const ownershipHistoryLength = currentVehicle?.ownership_history.length || 0;
  const titleRecords =
    (currentVehicle?.title_registrations as AutomobileTitleRegistrations) || [];
  // Put all the years from the title records an array
  const titleYears: number[] = titleRecords
    .map((title) => [
      title.date?.parsed?.year,
      ...title.history.map(
        (item: AutomobileTitleRegistrationCore) => item.date?.parsed?.year
      ),
    ])
    .flat()
    .filter((year): year is number => year !== null && year !== undefined);
  // Add the year of the first ownership history record
  if (currentVehicle?.ownership_history?.[0]?.data_date?.parsed?.year) {
    titleYears.push(currentVehicle.ownership_history[0].data_date.parsed.year);
  }

  const isUpgradedAndSuccess =
    vehicleReport.report_upgraded && currentVehicle?.nmvtis_success;
  // Count the number of owners for each mileage change
  const odometerValues: number[] = [];
  const ownersByTitleRecords = titleRecords.reduce(
    (total: number, title: AutomobileTitleRegistration) => {
      const itemsWithMileage = title.history.reduce((count, item) => {
        if (item.odometer != null && !odometerValues.includes(item.odometer)) {
          odometerValues.push(item.odometer);
          return count + 1;
        }
        return count;
      }, 0);

      const titleWithMileageCount =
        title.odometer != null && !odometerValues.includes(title.odometer)
          ? 1
          : 0;

      return total + itemsWithMileage + titleWithMileageCount;
    },
    0
  );

  const year = currentVehicle?.year;
  const noYear = !year;
  const avgDrivenMileage = 13500;
  const drivenMileage = currentVehicle?.mileage?.miles || 0;
  const ageOfVehicle = year ? new Date().getFullYear() - year : 0;

  const aboveAverageMileage =
    noYear || drivenMileage > avgDrivenMileage * ageOfVehicle;

  const owners = isUpgradedAndSuccess
    ? Math.max(ownersByTitleRecords, ownershipHistoryLength)
    : ownershipHistoryLength;
  const salvageCount =
    (currentVehicle?.salvagesCount || 0) +
    (currentVehicle?.titleChecksCount || 0);
  const damageReportedCount =
    currentVehicle?.damage_history?.reported_damage.length ||
    0 ||
    currentVehicle?.auctionsDamage.length ||
    0;
  const damageCount = accidentsCount + damageReportedCount;
  const damagePrefix =
    damageReportedCount && !accidentsCount
      ? `${damageReportedCount} ${pluralizeString(
          'Damage',
          damageReportedCount
        )}`
      : `${damageCount || 'No'} ${pluralizeString('Accident', accidentsCount)}`;

  const vehicleReportInfoCards = [
    {
      icon: (
        <ConditionalBadge
          recordedItem={damageCount > 0}
          icon={Icons.MinorCrash}
        />
      ),
      text: `${damagePrefix}`.trim(),
      link: 'vehicle-damage-history',
    },
    {
      icon: (
        <ConditionalBadge
          showBadge={owners > 0}
          recordedItem={owners > 1}
          customBadgeIconColor={owners > 1 && owners <= 3 ? '#ED6C02' : ''}
          customBoxBackgroundColor={owners > 1 && owners <= 3 ? '#FDF4EB' : ''}
          icon={owners > 1 ? Icons.PeopleOutlineRounded : Icons.PersonRounded}
        />
      ),
      text: `${owners} ${pluralizeString('Owner', owners)} Reported`,
    },
    {
      icon: (
        <ConditionalBadge
          recordedItem={aboveAverageMileage}
          icon={Icons.Speed}
        />
      ),
      text: `${commaFormat(drivenMileage)} ${pluralizeString(
        'Mile',
        drivenMileage
      )}`,
      link: 'vehicle-recalls',
    },
    {
      icon: (
        <ConditionalBadge
          showBadge={isUpgradedAndSuccess}
          recordedItem={salvageCount > 0}
          icon={Icons.HistoryEduOutlined}
        />
      ),
      text: !isUpgradedAndSuccess
        ? 'Unlock Titles'
        : salvageCount > 0
        ? 'Branded Title'
        : 'Clean Title',
      link:
        currentVehicle?.salvagesCount > 0 &&
        currentVehicle?.titleChecksCount === 0
          ? 'vehicle-damage-history'
          : 'vehicle-title-brands',
    },
  ];

  return (
    <ReportSection
      supertitleText="Vehicle Value"
      showTopBar
      title="Possible Vehicle Value"
      hasContent={historyBasedValue > 0 && !isYMMSearch}
      sectionId="vehicle-value"
      customEmptyText="No possible market value records found. Please note that the absence of any information or results within our reports may not necessarily be 100% accurate, complete, or up to date, so please do not use it in lieu of your own due diligence."
    >
      <Stack
        sx={{ alignItems: isMobile ? 'flex-start' : 'center' }}
        ref={elementRef}
      >
        <Text variant="body1" display="flex" alignItems="center" mt={2}>
          History-based Value
          <HistoryBasedValueTooltip
            icon={<Icons.Info sx={{ opacity: '50%', height: '15px' }} />}
          />
        </Text>
        <Stack sx={{ alignItems: isMobile ? 'flex-start' : 'center' }}>
          <Text fontSize="2rem" variant="h1" mt={1}>
            {commaFormatCurrency(historyBasedValue)}
          </Text>
          <VSIHistoryBasedText
            historyBasedValue={historyBasedValue}
            averageMarketValue={averageMarketValue}
          />
        </Stack>
        {!isMobile && (
          <Box
            sx={{
              width: '60%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box sx={{ position: 'relative', top: '6px' }}>
              <Polygon h={23} w={23} />
            </Box>

            <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', width: '100%' }} />
          </Box>
        )}
        <Stack
          mt={isMobile ? 4 : 0}
          mb={4}
          direction={isMobile ? 'column' : 'row'}
          sx={{ width: '80%', justifyContent: 'space-between' }}
        >
          {vehicleReportInfoCards.map((card) => {
            return (
              <Stack
                key={card.text}
                mt={isMobile ? 2 : 0}
                direction={isMobile ? 'row' : 'column'}
                alignItems="center"
                width={isMobile ? '100%' : '25%'}
              >
                {!isMobile && (
                  <Box
                    sx={{
                      border: '1px solid rgba(0,0,0,0.12)',
                      height: '2rem',
                    }}
                  />
                )}
                <Box mt={isMobile ? 0 : 2}>{card.icon}</Box>
                <Text
                  variant="body2"
                  fontWeight="bold"
                  mt={1}
                  ml={isMobile ? 2 : 0}
                  textAlign="center"
                >
                  {card.text}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </ReportSection>
  );
}
